import { HintDot } from '../help/HintDot'
import { useT } from '../i18n'
import type { DriftStatus } from './driftStatus'

interface DriftControlsProps {
  /** Дата последних доступных течений (из meta.currents_through); null — течений ещё нет. */
  currentsThrough: string | null
  /** Подсказка/бейдж статуса (загрузка/пусто/сводка со статусами). */
  status: DriftStatus | null
}

// Цвет строки-статуса по тону.
const STATUS_CLASS: Record<DriftStatus['tone'], string> = {
  muted: 'text-mist',
  warn: 'text-buoy',
  empty: 'text-faint',
}

/** Состояние лагранжева сноса: откуда течения и что посчитано.
 *
 *  Ползунка «Свежесть» здесь больше нет (решение владельца 11.09.2026): окно
 *  наблюдений одно — семь суток до выбранного дня, — и картину задаёт один
 *  ползунок дней −7…+2 внизу карты. Две ручки времени рисовали треки разной
 *  длины рядом с семисуточными усиками HSI, и это читалось как один слой.
 *
 *  Ни своей складной секции, ни выключателя, ни выбора вида здесь нет: раздел
 *  «По течениям» ровно про снос, а вид — общая настройка раздела (её держит
 *  ModelsPage рядом с датой и глубиной). */
export function DriftControls({ currentsThrough, status }: DriftControlsProps) {
  const t = useT()
  return (
    <div className="space-y-1.5 border-t border-hull-hair pt-2.5">
      {/* Глубина сноса зависит от вида, и это не видно ниоткуда: подсказка —
          единственное место, где об этом сказано. */}
      <div className="flex items-center gap-1.5 text-[11px] text-faint">
        {t('models.drift.byObservationDate')}
        <HintDot id="driftDepth" />
      </div>

      {/* Дата всегда «последние течения» — тихая подпись, не параметр расчёта. */}
      <div className="text-xs text-faint">
        {t('models.drift.currentsOn')} <span className="tnum text-mist">{currentsThrough ?? '—'}</span>
      </div>

      {/* Ненавязчивый бейдж статуса (загрузка/пусто/устаревшие течения). */}
      {status && (
        <div className={['text-xs leading-snug', STATUS_CLASS[status.tone]].join(' ')} role="status">
          {status.parts.map(part => t(part.key, part.vars)).join(' · ')}
        </div>
      )}
    </div>
  )
}
